import { useEffect, useState } from 'react';
import CampoImporto from '../components/CampoImporto';
import { useNavigate } from 'react-router-dom';
import { offerteApi, clientiApi, authApi, consulentiApi, listinoApi } from '../api';
import Modal from '../components/Modal';
import StatusBadge from '../components/StatusBadge';
import ClienteAutocomplete from '../components/ClienteAutocomplete';
import PannelloListino from '../components/PannelloListino';
import PannelloPreventivo from '../components/PannelloPreventivo';
import { giornatePreventivo } from '../components/calcoloPreventivo';
import GeneraDocumento from '../components/GeneraDocumento';
import PannelloDocumento, { docVuoto, daTesto, totaleGiornate, impegnoListino } from '../components/PannelloDocumento';
import VersioniOfferta from '../components/VersioniOfferta';
import RinnovoOfferta from '../components/RinnovoOfferta';
import SceltaDaRinnovare from '../components/SceltaDaRinnovare';
import ImportaOffertaEsterna from '../components/ImportaOffertaEsterna';
import PannelloAllegati, { caricaInSospeso } from '../components/PannelloAllegati';
import ModaleAllegati from '../components/ModaleAllegati';
import { useAuth } from '../context/AuthContext';

const empty = { cliente_id: '', user_id: '', numero: '', titolo: '', descrizione: '', importo: '', stato: 'bozza', data_emissione: '', data_scadenza: '', responsabile: '', note: '', modalita_esecuzione: 'corpo' };
const emptyCliente = { nome: '', ragione_sociale: '', email: '', telefono: '', piva: '' };
const STATI = ['bozza', 'inviata', 'vinta', 'persa', 'scaduta'];
const MESI = ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
              'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'];
const fmt = n => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n || 0);
// "ottobre 2026": il mese in cui una fattura cadrà, scritto come lo si legge
const meseDi = (d) => d
  ? new Date(d).toLocaleDateString('it-IT', { month: 'long', year: 'numeric' })
  : 'questo mese';

export default function Offerte() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [clienti, setClienti] = useState([]);
  const [utenti, setUtenti] = useState([]);
  const [filtroStato, setFiltroStato] = useState('');
  const [filtroCliente, setFiltroCliente] = useState('');
  const [ricerca, setRicerca] = useState('');
  // Anno, mese e autore si filtrano qui e non sul server: le offerte sono
  // poche e già caricate, e il filtro reagisce senza attese.
  const [filtroAutore, setFiltroAutore] = useState('');
  const [filtroAnno, setFiltroAnno] = useState('');
  const [filtroMese, setFiltroMese] = useState('');
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [loadingNumero, setLoadingNumero] = useState(false);
  const [consulenti, setConsulenti] = useState([]);
  const [conv, setConv] = useState(null);          // stato del modale di conversione
  const [convertendo, setConvertendo] = useState(false);
  const [voci, setVoci] = useState([]);            // listino a economia / tariffe aggiuntive a corpo
  const [docOfferta, setDocOfferta] = useState(null);   // offerta per cui generare il documento
  const [doc, setDoc] = useState(docVuoto());           // contenuti del documento, salvati con l'offerta
  const [allegatiSospesi, setAllegatiSospesi] = useState([]);
  const [rinnovo, setRinnovo] = useState(null);   // offerta da rinnovare, solo su richiesta
  const [sceltaRinnovo, setSceltaRinnovo] = useState(false);   // da quale offerta partire
  const [allegatiDi, setAllegatiDi] = useState(null);          // offerta di cui gestire gli allegati

  const [showNuovoCliente, setShowNuovoCliente] = useState(false);
  const [nuovoCliente, setNuovoCliente] = useState(emptyCliente);
  const [savingCliente, setSavingCliente] = useState(false);

  const navigate = useNavigate();

  const load = () => offerteApi.list({
    stato: filtroStato || undefined,
    cliente_id: filtroCliente || undefined,
  }).then(setRows);
  const loadClienti = () => clientiApi.list().then(setClienti);
  useEffect(() => { load(); }, [filtroStato, filtroCliente]);
  useEffect(() => {
    loadClienti();
    authApi.usersList().then(setUtenti);
    consulentiApi.list().then(setConsulenti).catch(() => setConsulenti([]));
  }, []);

  // L'anno del numero segue la data di emissione dell'offerta, non la data di oggi.
  // forza=true serve al pulsante di ricalcolo: riprende il progressivo anche dopo
  // che il numero è stato corretto a mano.
  const generaNumero = async (userId, dataEmissione, forza = false) => {
    if (!userId) return;
    setLoadingNumero(true);
    try {
      const anno = dataEmissione ? Number(dataEmissione.slice(0, 4)) : undefined;
      const res = await offerteApi.nextNumero(userId, anno);
      setForm(p => (p.numeroManuale && !forza) ? p : { ...p, numero: res.numero, numeroManuale: false });
    } catch { /* lascia il numero invariato se errore */ }
    setLoadingNumero(false);
  };

  const openNew = async () => {
    const uid = user?.id || '';
    setForm({ ...empty, user_id: uid, responsabile: user?.nome_completo || '' });
    setVoci([]);
    setDoc(docVuoto());
    setAllegatiSospesi([]);
    setShowNuovoCliente(false);
    setModal({ mode: 'new' });
    if (uid) await generaNumero(uid);
  };

  const openEdit = (r) => {
    setForm({ ...r, user_id: r.user_id || '', data_emissione: r.data_emissione?.slice(0,10)||'', data_scadenza: r.data_scadenza?.slice(0,10)||'' });
    setVoci([]);
    setDoc({ ...docVuoto(), ...(r.documento || {}) });
    setAllegatiSospesi([]);
    setShowNuovoCliente(false);
    setModal({ mode: 'edit', id: r.id });
    // Le tariffe servono in entrambe le modalità: a economia sono il listino
    // del lavoro previsto, a corpo quelle degli interventi aggiuntivi.
    listinoApi.get('offerta', r.id).then(setVoci).catch(() => setVoci([]));
  };

  const onUtenteChange = (userId) => {
    const u = utenti.find(x => String(x.id) === String(userId));
    setForm(p => ({ ...p, user_id: userId, responsabile: u?.nome_completo || p.responsabile }));
    if (modal?.mode === 'new') generaNumero(userId, form.data_emissione);
  };

  // Cambiando l'anno di emissione cambia anche l'anno nel numero: un'offerta
  // datata 2024 deve diventare FB0124, non restare sull'anno corrente.
  const onDataEmissioneChange = (val) => {
    const annoPrec = form.data_emissione?.slice(0, 4);
    setForm(p => ({ ...p, data_emissione: val }));
    if (modal?.mode === 'new' && val?.slice(0, 4) !== annoPrec) generaNumero(form.user_id, val);
  };

  // genera=true: dopo il salvataggio si apre subito la generazione del
  // documento. Chi ha appena compilato i contenuti vuole l'offerta da inviare,
  // e l'icona 📄 in fondo alla riga dell'elenco non la trovava nessuno.
  const save = async (genera = false) => {
    setSaving(true);
    try {
      if (showNuovoCliente && nuovoCliente.nome) {
        const creato = await clientiApi.create(nuovoCliente);
        await loadClienti();
        form.cliente_id = String(creato.id);
        setForm(p => ({ ...p, cliente_id: String(creato.id) }));
        setNuovoCliente(emptyCliente);
        setShowNuovoCliente(false);
      }
      // numeroManuale serve solo al modulo, non è un campo dell'offerta
      const { numeroManuale, ...dati } = form;
      // I contenuti del documento e le giornate previste viaggiano con l'offerta
      dati.documento = doc;
      // Dalle fasi o dal listino; se mancano, dal preventivo
      dati.giornate_previste = totaleGiornate(doc, voci)
        || (doc.preventivo?.attivo ? giornatePreventivo(doc.preventivo) : 0) || null;
      const salvata = modal.mode === 'new'
        ? await offerteApi.create({ ...dati, cliente_id: dati.cliente_id || undefined })
        : await offerteApi.update(modal.id, dati);

      // Il listino si salva dopo l'offerta, perché in creazione l'id nasce ora.
      // Vale in entrambe le modalità: a economia è il listino del lavoro
      // previsto, a corpo sono le tariffe degli interventi aggiuntivi.
      const idOfferta = modal.mode === 'new' ? salvata?.id : modal.id;
      if (idOfferta) {
        await listinoApi.save('offerta', idOfferta, voci.filter(v => v.descrizione?.trim()));
        // Gli allegati scelti prima del salvataggio si caricano ora che c'è l'id
        if (allegatiSospesi.length) await caricaInSospeso('offerta', idOfferta, allegatiSospesi);
      }
      setModal(null); load();
      if (genera && idOfferta) setDocOfferta(await offerteApi.get(idOfferta));
    } catch (e) { alert(e); } finally { setSaving(false); }
  };

  // Un'offerta diventata commessa è un contratto: eliminandola la commessa
  // resta (scollegata), ma se ne vanno allegati come l'ordine o l'offerta
  // firmata. Va detto prima.
  const del = async (r) => {
    const msg = r.ha_commessa
      ? `L'offerta ${r.numero} è già diventata la commessa ${r.commessa_numero || ''}.\n\n`
        + 'Eliminandola la commessa resta, con fatture e SAL, ma si perdono gli allegati dell\'offerta '
        + '(ordine, offerta firmata) e il suo listino.\n\nEliminare comunque?'
      : `Eliminare l'offerta ${r.numero}?`;
    if (!confirm(msg)) return;
    try { await offerteApi.delete(r.id); load(); } catch (e) { alert(e); }
  };

  const apriConversione = (r) => {
    setConv({
      offerta: r,
      numero_commessa: r.numero,
      responsabile: '',                 // "u:<id>" interno · "c:<id>" esterno
      data_inizio: new Date().toISOString().slice(0, 10),
      tipo_fatturazione: '',
      periodicita_mesi: '',
      modalita_esecuzione: r.modalita_esecuzione || 'corpo',
      voci: [],
      acconto: null, acconto_importo: '',
    });
    // Condizioni concordate nell'offerta: importo, modalità e periodicità di
    // fatturazione arrivano già compilate, così non si reinseriscono a mano.
    offerteApi.condizioni(r.id).then(c => setConv(x => x ? {
      ...x,
      tipo_fatturazione: x.tipo_fatturazione || c.tipo_fatturazione || '',
      periodicita_mesi: x.periodicita_mesi || (c.periodicita_mesi ?? ''),
      modalita_esecuzione: c.modalita_esecuzione || x.modalita_esecuzione,
      ereditate: !!(c.tipo_fatturazione || c.periodicita_mesi),
      // Acconto letto dalle condizioni di pagamento: proposto, non imposto
      acconto: c.acconto || null,
      acconto_importo: c.acconto?.importo ? String(c.acconto.importo) : '',
    } : x)).catch(() => {});

    // Se l'offerta ha già delle tariffe le si porta in conversione: a economia
    // per rivederle, a corpo per mostrare cosa erediterà la commessa.
    listinoApi.get('offerta', r.id)
      .then(v => setConv(c => c ? { ...c, voci: v } : c))
      .catch(() => {});
  };

  const confermaConversione = async () => {
    if (!conv.numero_commessa.trim()) return alert('Indica il numero della commessa');
    const [tipo, id] = (conv.responsabile || '').split(':');
    setConvertendo(true);
    try {
      const cm = await offerteApi.converti(conv.offerta.id, {
        numero_commessa: conv.numero_commessa.trim(),
        user_id: tipo === 'u' ? Number(id) : undefined,
        consulente_id: tipo === 'c' ? Number(id) : undefined,
        data_inizio: conv.data_inizio || undefined,
        tipo_fatturazione: conv.tipo_fatturazione || undefined,
        periodicita_mesi: conv.periodicita_mesi === '' ? undefined : Number(conv.periodicita_mesi),
        modalita_esecuzione: conv.modalita_esecuzione,
        // false = nessun acconto, anche se le condizioni ne prevedrebbero uno
        acconto: Number(conv.acconto_importo) > 0 ? Number(conv.acconto_importo) : false,
      });
      // Le voci riviste in conversione diventano il listino della commessa
      // (sostituiscono l'eventuale copia automatica dal listino dell'offerta).
      if (conv.modalita_esecuzione === 'economia') {
        await listinoApi.save('commessa', cm.id, conv.voci.filter(v => v.descrizione?.trim()));
      }
      setConv(null);
      // Le offerte dei consulenti nate dal preventivo vanno riviste; e se è
      // nata la fattura di acconto va detto dove andarla a spuntare.
      const avvisi = [];
      if (cm.acconto) {
        avvisi.push(`L'acconto di ${fmt(cm.acconto.importo)} è fra le fatture da emettere di `
          + `${meseDi(cm.acconto.competenza)}: spuntalo nel Riepilogo mese quando l'avrai emesso.`);
      }
      if (cm.offerte_consulenti_create > 0) {
        avvisi.push(`Dal preventivo sono nate ${cm.offerte_consulenti_create} offerte consulente `
          + `da approvare: le trovi in "Offerte consulenti".`);
      }
      if (avvisi.length) alert(`Commessa creata.\n\n${avvisi.join('\n\n')}`);
      navigate(`/commesse/${cm.id}`);
    } catch (e) { alert(e); } finally { setConvertendo(false); }
  };

  const salvaNuovoCliente = async () => {
    if (!nuovoCliente.nome) return alert('Inserire almeno il nome');
    setSavingCliente(true);
    try {
      const creato = await clientiApi.create(nuovoCliente);
      await loadClienti();
      setForm(p => ({ ...p, cliente_id: String(creato.id) }));
      setNuovoCliente(emptyCliente);
      setShowNuovoCliente(false);
    } catch (e) { alert(e); } finally { setSavingCliente(false); }
  };

  // Le due metà del pannello documento scrivono sullo stesso oggetto: il
  // modello propone anche l'oggetto dell'offerta, che si copia nel titolo solo
  // se il titolo è ancora vuoto, per non sovrascrivere quello che si è scritto.
  const aggiornaDoc = (d) => {
    if (d.oggettoProposto && !form.titolo?.trim()) {
      setForm(p => ({ ...p, titolo: d.oggettoProposto }));
    }
    const { oggettoProposto, ...pulito } = d;
    setDoc(pulito);
  };

  const f = (k) => ({ value: form[k] || '', onChange: e => setForm(p => ({ ...p, [k]: e.target.value })) });
  const preventivoAttivo = form.modalita_esecuzione !== 'economia' && !!doc.preventivo?.attivo;

  // Il prezzo si scrive nella finestra del preventivo, non fra i primi dati.
  // Con il preventivo attivo scriverlo significa arrotondare: il calcolo lo
  // riporta nel dettaglio (sulla riga di sconto, se c'è, altrimenti sui prezzi
  // unitari). Svuotando il campo si torna al prezzo calcolato.
  const cambiaImporto = (v) => {
    setForm(p => ({ ...p, importo: v }));
    if (preventivoAttivo) setDoc(d => ({ ...d, preventivo: { ...d.preventivo, prezzoFinale: v } }));
  };
  const fc = (k) => ({ value: nuovoCliente[k] || '', onChange: e => setNuovoCliente(p => ({ ...p, [k]: e.target.value })) });

  // Gli anni proposti sono quelli che esistono davvero fra le offerte: un
  // elenco fisso di annate vuote non serve a nessuno.
  const anni = [...new Set(rows
    .map(r => r.data_emissione && new Date(r.data_emissione).getFullYear())
    .filter(Boolean))].sort((a, b) => b - a);

  const visibili = rows.filter(r => {
    const q = ricerca.trim().toLowerCase();
    if (q && !`${r.numero} ${r.titolo} ${r.cliente_nome || ''}`.toLowerCase().includes(q)) return false;
    if (filtroAutore && String(r.user_id) !== String(filtroAutore)) return false;
    if (filtroAnno || filtroMese) {
      // Un'offerta senza data non si può collocare nel tempo: chiedendo un
      // periodo, resta fuori.
      if (!r.data_emissione) return false;
      const d = new Date(r.data_emissione);
      if (filtroAnno && d.getFullYear() !== Number(filtroAnno)) return false;
      if (filtroMese && d.getMonth() + 1 !== Number(filtroMese)) return false;
    }
    return true;
  });

  const filtriAttivi = !!(filtroStato || filtroCliente || ricerca.trim() || filtroAutore || filtroAnno || filtroMese);
  const azzeraFiltri = () => {
    setFiltroStato(''); setFiltroCliente(''); setRicerca('');
    setFiltroAutore(''); setFiltroAnno(''); setFiltroMese('');
  };

  const kanban = STATI.map(s => ({ stato: s, items: visibili.filter(r => r.stato === s) }));

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="page-title">Offerte</h1>
        <div className="flex items-center gap-2">
          {/* Il rinnovo parte da qui, non solo dall'icona sulla riga: a
              gennaio si pensa "quali contratti vanno rinnovati?", non alla
              singola offerta. L'icona gira al passaggio del mouse, così si
              nota senza strillare. */}
          <button className="btn-secondary group" onClick={() => setSceltaRinnovo(true)}
                  title="Rinnova un'offerta per l'anno successivo">
            <span className="inline-block transition-transform duration-500 group-hover:rotate-180">♻</span>
            <span className="ml-1.5">Rinnova offerta</span>
          </button>
          <ImportaOffertaEsterna clienti={clienti} onCreata={load} />
          <button className="btn-primary" onClick={openNew}>+ Nuova offerta</button>
        </div>
      </div>

      <div className="flex gap-2 flex-wrap items-center">
        {['', ...STATI].map(s => (
          <button key={s} onClick={() => setFiltroStato(s)}
            className={`btn-sm btn ${filtroStato === s ? 'bg-brand-600 text-white' : 'bg-white text-gray-600 border border-gray-300 hover:bg-gray-50'}`}>
            {s || 'Tutti'}
          </button>
        ))}
        <select className="input py-1 text-sm max-w-[240px]" value={filtroCliente} onChange={e => setFiltroCliente(e.target.value)}>
          <option value="">Tutti i clienti</option>
          {clienti.map(c => <option key={c.id} value={c.id}>{c.ragione_sociale || c.nome}</option>)}
        </select>
        <input className="input py-1 text-sm max-w-[220px]" placeholder="Cerca numero o titolo…"
               value={ricerca} onChange={e => setRicerca(e.target.value)} />

        {/* Chi l'ha inserita e quando: sono le due domande con cui si cerca
            un'offerta quando non se ne ricorda il numero. */}
        <select className="input py-1 text-sm max-w-[200px]" value={filtroAutore}
                onChange={e => setFiltroAutore(e.target.value)}>
          <option value="">Tutti gli autori</option>
          {utenti.map(u => (
            <option key={u.id} value={u.id}>
              {[u.nome, u.cognome].filter(Boolean).join(' ') || u.username}
            </option>
          ))}
        </select>

        <select className="input py-1 text-sm max-w-[130px]" value={filtroAnno}
                onChange={e => setFiltroAnno(e.target.value)}>
          <option value="">Tutti gli anni</option>
          {anni.map(a => <option key={a} value={a}>{a}</option>)}
        </select>

        <select className="input py-1 text-sm max-w-[150px]" value={filtroMese}
                onChange={e => setFiltroMese(e.target.value)}>
          <option value="">Tutti i mesi</option>
          {MESI.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
        </select>

        {filtriAttivi && (
          <button className="btn-sm btn bg-white text-gray-600 border border-gray-300 hover:bg-gray-50"
                  onClick={azzeraFiltri} title="Rimuovi tutti i filtri">✕ Azzera filtri</button>
        )}
      </div>

      {/* Conteggi per stato */}
      <div className="flex gap-4 flex-wrap text-xs text-gray-500 items-center">
        {kanban.map(col => (
          <span key={col.stato} className="flex items-center gap-1.5">
            <StatusBadge stato={col.stato} /> {col.items.length}
          </span>
        ))}
        <span className="ml-auto">
          {/* Quante se ne stanno guardando rispetto al totale: con più filtri
              attivi è l'unico modo per accorgersi che l'elenco è parziale. */}
          <strong className="text-gray-700">{visibili.length}</strong>
          {visibili.length !== rows.length && <> di {rows.length}</>} offerte ·
          {' '}Totale visualizzato:{' '}
          <strong className="text-gray-700">{fmt(visibili.reduce((s, r) => s + parseFloat(r.importo || 0), 0))}</strong>
        </span>
      </div>

      <div className="card p-0 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b">
            <tr className="text-left text-xs text-gray-500">
              <th className="px-4 py-3">Numero</th>
              <th className="px-4 py-3">Titolo</th>
              <th className="px-4 py-3">Cliente</th>
              <th className="px-4 py-3 text-right">Importo</th>
              <th className="px-4 py-3 text-right">Giornate</th>
              <th className="px-4 py-3">Stato</th>
              <th className="px-4 py-3">Emissione</th>
              <th className="px-4 py-3">Inserita da</th>
              <th className="px-3 py-3 sticky right-0 bg-gray-50 text-right">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {visibili.length === 0 && <tr><td colSpan={9} className="px-4 py-8 text-center text-gray-400">Nessuna offerta</td></tr>}
            {visibili.map(r => (
              <tr key={r.id} className={`border-b last:border-0 hover:bg-gray-50 ${r.stato === 'vinta' ? 'bg-green-50/50' : r.stato === 'persa' ? 'bg-red-50/40' : ''}`}>
                <td className="px-4 py-3 font-mono text-gray-600">{r.numero}</td>
                <td className="px-4 py-3 font-medium text-gray-900">
                  <button className="text-left hover:text-brand-600 hover:underline" onClick={() => openEdit(r)}>{r.titolo}</button>
                </td>
                <td className="px-4 py-3 text-gray-600">{r.cliente_nome}</td>
                <td className="px-4 py-3 font-medium text-right">{fmt(r.importo)}</td>
                <td className="px-4 py-3 text-right text-gray-600">
                  {Number(r.giornate_previste) > 0
                    ? String(Number(r.giornate_previste) % 1 === 0 ? Number(r.giornate_previste) : Number(r.giornate_previste).toFixed(1)).replace('.', ',')
                    : <span className="text-gray-300">—</span>}
                </td>
                <td className="px-4 py-3"><StatusBadge stato={r.stato} /></td>
                <td className="px-4 py-3 text-gray-500 text-xs">{r.data_emissione ? new Date(r.data_emissione).toLocaleDateString('it-IT') : '—'}</td>
                <td className="px-4 py-3 text-gray-500 text-xs">{r.user_nome || '—'}</td>
                {/* Colonna fissa sul bordo destro: con la tabella più larga dello
                    schermo (portatili) i pulsanti finivano fuori vista oltre lo
                    scorrimento, e sembrava che mancassero. Resta una cella di
                    tabella — renderla "flex" le faceva perdere l'allineamento. */}
                <td className={`px-3 py-2 align-middle sticky right-0 shadow-[-6px_0_6px_-6px_rgba(0,0,0,0.15)] ${
                  r.stato === 'vinta' ? 'bg-green-50' : r.stato === 'persa' ? 'bg-red-50' : 'bg-white'}`}>
                  <div className="flex flex-col items-end gap-1">
                    {/* Si converte tutto ciò che non è perso e non ha già una commessa:
                        sono proprio le vinte importate dallo storico il caso principale. */}
                    {r.ha_commessa ? (
                      <span className="text-xs text-green-700 whitespace-nowrap font-medium"
                            title={`Commessa ${r.commessa_numero || ''}`}>
                        ✓ {r.commessa_numero || 'commessa'}
                      </span>
                    ) : r.stato !== 'persa' && r.stato !== 'scaduta' && (
                      <button
                        className="btn-sm btn bg-green-100 text-green-700 hover:bg-green-200 font-semibold whitespace-nowrap"
                        title="Trasforma l'offerta in commessa"
                        onClick={() => apriConversione(r)}
                      >→ Commessa</button>
                    )}
                    <div className="flex gap-1">
                      <button className="btn-secondary btn-sm px-2" title="Genera il documento dell'offerta (Word/PDF)"
                              onClick={() => setDocOfferta(r)}>📄</button>
                      <button className="btn-secondary btn-sm px-2" title="Allegati: ordine di acquisto, offerta firmata"
                              onClick={() => setAllegatiDi(r)}>📎</button>
                      {/* Rinnovo per l'anno dopo: si fa solo da qui, offerta
                          per offerta. Niente rinnovi a calendario. */}
                      <button className="btn-secondary btn-sm px-2"
                              title="Rinnova per l'anno successivo (copia contenuti e prezzi)"
                              onClick={() => setRinnovo(r)}>♻</button>
                      <button className="btn-secondary btn-sm px-2" title="Modifica l'offerta"
                              onClick={() => openEdit(r)}>✎</button>
                      <button className="btn-danger btn-sm px-2" title="Elimina l'offerta"
                              onClick={() => del(r)}>🗑</button>
                    </div>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modal && (
        <Modal title={modal.mode === 'new' ? 'Nuova offerta' : 'Modifica offerta'} onClose={() => setModal(null)}
               size="xl">
          <div className="grid grid-cols-2 gap-4">

            {/* Cliente */}
            <div className="col-span-2">
              <label className="label">Cliente</label>
              <div className="flex gap-2">
                <select
                  className="input flex-1"
                  value={form.cliente_id || ''}
                  onChange={e => setForm(p => ({ ...p, cliente_id: e.target.value }))}
                >
                  <option value="">— Seleziona —</option>
                  {clienti.map(c => <option key={c.id} value={c.id}>{c.ragione_sociale || c.nome}</option>)}
                </select>
                <button
                  type="button"
                  className="btn-secondary whitespace-nowrap"
                  onClick={() => setShowNuovoCliente(v => !v)}
                >
                  {showNuovoCliente ? '✕ Annulla' : '+ Nuovo cliente'}
                </button>
              </div>

              {showNuovoCliente && (
                <div className="mt-3 p-4 bg-blue-50 border border-blue-200 rounded-lg space-y-3">
                  <p className="text-xs font-semibold text-blue-700 uppercase tracking-wide">Nuovo cliente</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="col-span-2">
                      <label className="label text-xs">Ragione Sociale *</label>
                      <ClienteAutocomplete
                        value={nuovoCliente.ragione_sociale || ''}
                        onChange={val => setNuovoCliente(p => ({ ...p, ragione_sociale: val }))}
                        onSelect={c => {
                          setForm(p => ({ ...p, cliente_id: String(c.id) }));
                          setNuovoCliente(emptyCliente);
                          setShowNuovoCliente(false);
                        }}
                        placeholder="Es. Acme S.r.l. — se già presente verrà selezionato"
                      />
                    </div>
                    <div>
                      <label className="label text-xs">Nome breve / Alias</label>
                      <input className="input" placeholder="(facoltativo)" {...fc('nome')} />
                    </div>
                    <div>
                      <label className="label text-xs">Email</label>
                      <input className="input" type="email" {...fc('email')} />
                    </div>
                    <div>
                      <label className="label text-xs">Telefono</label>
                      <input className="input" {...fc('telefono')} />
                    </div>
                    <div>
                      <label className="label text-xs">P.IVA</label>
                      <input className="input" {...fc('piva')} />
                    </div>
                    <div className="flex items-end">
                      <button
                        type="button"
                        className="btn-primary w-full"
                        onClick={salvaNuovoCliente}
                        disabled={savingCliente}
                      >
                        {savingCliente ? 'Salvo...' : '✓ Crea e seleziona'}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Utente che emette l'offerta — determina le iniziali del progressivo */}
            <div className="col-span-2">
              <label className="label">
                Utente che emette l'offerta *
                <span className="ml-1 text-xs text-gray-400 font-normal">(determina le iniziali del numero offerta)</span>
              </label>
              <select
                className="input"
                value={form.user_id || ''}
                onChange={e => onUtenteChange(e.target.value)}
              >
                <option value="">— Seleziona —</option>
                {utenti.map(u => (
                  <option key={u.id} value={u.id}>{u.nome_completo} {u.iniziali ? `(${u.iniziali})` : ''}</option>
                ))}
              </select>
            </div>

            {/* Numero offerta: proposto in automatico ma sempre correggibile a mano,
                perché servono anche i casi fuori standard (revisioni, riferimenti cliente). */}
            <div>
              <label className="label">
                Numero offerta *
                <span className="ml-1 text-xs text-gray-400 font-normal">
                  {modal.mode === 'new' ? '(proposto, modificabile)' : ''}
                </span>
              </label>
              <div className="flex gap-1.5">
                <input
                  className="input font-mono"
                  value={loadingNumero ? '...' : (form.numero || '')}
                  onChange={e => setForm(p => ({ ...p, numero: e.target.value, numeroManuale: true }))}
                />
                {modal.mode === 'new' && (
                  <button type="button" className="btn-secondary shrink-0" title="Ricalcola il progressivo"
                          onClick={() => generaNumero(form.user_id, form.data_emissione, true)}>
                    ↻
                  </button>
                )}
              </div>
            </div>
            <div>
              <label className="label">Responsabile</label>
              <input className="input" {...f('responsabile')} />
            </div>
            <div className="col-span-2"><label className="label">Titolo *</label><input className="input" {...f('titolo')} /></div>
            <div>
              <label className="label">Stato</label>
              <select className="input" value={form.stato} onChange={e => setForm(p => ({ ...p, stato: e.target.value }))}>
                {STATI.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
              </select>
            </div>
            <div>
              <label className="label">Data emissione</label>
              <input className="input" type="date" value={form.data_emissione || ''}
                     onChange={e => onDataEmissioneChange(e.target.value)} />
            </div>
            <div><label className="label">Data scadenza</label><input className="input" type="date" {...f('data_scadenza')} /></div>
            <div>
              <label className="label">Modalità</label>
              <select className="input" value={form.modalita_esecuzione || 'corpo'}
                      onChange={e => setForm(p => ({ ...p, modalita_esecuzione: e.target.value }))}>
                <option value="corpo">A corpo (prezzo fisso)</option>
                <option value="economia">A economia (a consuntivo)</option>
              </select>
            </div>
            <div className="flex items-end">
              <p className="text-xs text-gray-500 pb-2">
                {form.modalita_esecuzione === 'economia'
                  ? 'Si rendicontano giornate e spese sulle tariffe del listino qui sotto.'
                  : 'Importo fisso: l’avanzamento si misura in percentuale sull’offerta.'}
              </p>
            </div>

            {/* 1. Contenuti del documento: tipologia, template, attività.
                È da qui che si decide cosa dirà l'offerta, quindi viene prima
                del prezzo, che di quelle attività è la conseguenza. */}
            <div className="col-span-2">
              <PannelloDocumento
                parte="contenuti"
                doc={doc}
                onChange={aggiornaDoc}
                giornateListino={voci.reduce((s, v) => s + (Number(v.quantita_prevista) || 0), 0)}
                impegno={impegnoListino(voci)}
              />
            </div>

            {/* 2. Il prezzo, nella stessa finestra che lo calcola:
                a corpo il preventivo, a economia il listino. */}
            {form.modalita_esecuzione !== 'economia' ? (
              <div className="col-span-2">
                <PannelloPreventivo
                  preventivo={doc.preventivo}
                  consulenti={consulenti}
                  onChange={pr => setDoc(d => ({ ...d, preventivo: pr }))}
                  onImporto={v => setForm(p => (Number(p.importo) === v ? p : { ...p, importo: v }))}
                  importo={form.importo}
                  onImportoChange={cambiaImporto}
                />
              </div>
            ) : (
              <div className="col-span-2 space-y-3">
                <PannelloListino voci={voci} onChange={setVoci} />
                <div className="w-52">
                  <label className="label">Importo stimato (€)</label>
                  <CampoImporto className="input" value={form.importo} onChange={cambiaImporto} />
                </div>
              </div>
            )}

            {/* 3. Tariffe di quello che il cliente potrà chiedere in più:
                subito dopo il prezzo e prima dei tempi, perché si leggono
                insieme al preventivo. */}
            {form.modalita_esecuzione !== 'economia' && (
              <div className="col-span-2">
                <PannelloListino
                  voci={voci} onChange={setVoci}
                  mostraQuantita={false}
                  titolo="Tariffe per interventi aggiuntivi (facoltative)"
                  descrizione={"Il prezzo a corpo copre il lavoro previsto. Qui si indicano le "
                    + "tariffe di eventuali interventi in più, che il cliente dovrà approvare prima che "
                    + "vengano svolti: raggruppale in macro voci se il cliente ne sceglierà solo alcune."}
                />
              </div>
            )}

            {/* 4. Tempi e condizioni: cronoprogramma, fasi, pagamento. */}
            <div className="col-span-2">
              <PannelloDocumento
                parte="pianificazione"
                doc={doc}
                onChange={aggiornaDoc}
                giornateListino={voci.reduce((s, v) => s + (Number(v.quantita_prevista) || 0), 0)}
                impegno={impegnoListino(voci)}
              />
            </div>

            <div className="col-span-2">
              <PannelloAllegati
                tipo="offerta"
                id={modal.mode === 'edit' ? modal.id : null}
                inSospeso={allegatiSospesi}
                onInSospeso={setAllegatiSospesi}
              />
            </div>

            {/* Storia dell'offerta: le revisioni precedenti, quando ce ne sono. */}
            {modal.mode === 'edit' && (
              <div className="col-span-2">
                <VersioniOfferta offertaId={modal.id} />
              </div>
            )}

            <div className="col-span-2"><label className="label">Descrizione</label><textarea className="input" rows={2} {...f('descrizione')} /></div>
            <div className="col-span-2"><label className="label">Note</label><textarea className="input" rows={2} {...f('note')} /></div>
          </div>
          <div className="flex justify-end gap-2 mt-5">
            <button className="btn-secondary" onClick={() => setModal(null)}>Annulla</button>
            <button className="btn-secondary" onClick={() => save(true)} disabled={saving || loadingNumero}
                    title="Salva l'offerta e apri subito la generazione del documento Word/PDF da inviare">
              {saving ? 'Salvo...' : '📄 Salva e genera documento'}
            </button>
            <button className="btn-primary" onClick={() => save()} disabled={saving || loadingNumero}>{saving ? 'Salvo...' : 'Salva'}</button>
          </div>
        </Modal>
      )}

      {/* Conversione offerta → commessa */}
      {conv && (
        <Modal title="Trasforma in commessa" onClose={() => setConv(null)}
               size={conv.modalita_esecuzione === 'economia' ? 'xl' : 'lg'}>
          <div className="bg-green-50 border border-green-200 rounded-lg px-3 py-2 mb-4 text-sm">
            <div className="font-semibold text-gray-800">{conv.offerta.numero} — {conv.offerta.titolo}</div>
            <div className="text-xs text-gray-600 mt-0.5">
              {conv.offerta.cliente_nome} · {fmt(conv.offerta.importo)}
            </div>
            <div className="text-xs text-gray-500 mt-1">
              L'offerta verrà marcata come <strong>vinta</strong> e diventerà una commessa con questo budget.
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Numero commessa *</label>
              <input className="input font-mono" value={conv.numero_commessa}
                     onChange={e => setConv(c => ({ ...c, numero_commessa: e.target.value }))} />
            </div>
            <div>
              <label className="label">Data inizio</label>
              <input className="input" type="date" value={conv.data_inizio}
                     onChange={e => setConv(c => ({ ...c, data_inizio: e.target.value }))} />
            </div>

            <div className="col-span-2">
              <label className="label">
                Responsabile della commessa
                <span className="ml-1 text-xs text-gray-400 font-normal">(personale Sinthema o consulente esterno)</span>
              </label>
              <select className="input" value={conv.responsabile}
                      onChange={e => setConv(c => ({ ...c, responsabile: e.target.value }))}>
                <option value="">— Nessuno —</option>
                <optgroup label="Personale Sinthema">
                  {utenti.map(u => <option key={`u${u.id}`} value={`u:${u.id}`}>{u.nome_completo}</option>)}
                </optgroup>
                <optgroup label="Consulenti esterni">
                  {consulenti.map(c => (
                    <option key={`c${c.id}`} value={`c:${c.id}`}>
                      {c.nome} {c.cognome}{c.specializzazione ? ` — ${c.specializzazione}` : ''}
                    </option>
                  ))}
                </optgroup>
              </select>
            </div>

            <div className="col-span-2">
              <label className="label">
                Modalità di esecuzione
                <span className="ml-1 text-xs text-gray-400 font-normal">(determina cosa rendicontano i consulenti)</span>
              </label>
              <select className="input" value={conv.modalita_esecuzione}
                      onChange={e => setConv(c => ({ ...c, modalita_esecuzione: e.target.value }))}>
                <option value="corpo">A corpo — avanzamento in percentuale sull'offerta</option>
                <option value="economia">In economia — ore/giornate a consuntivo e spese</option>
              </select>
            </div>

            {conv.modalita_esecuzione !== 'economia' && conv.voci.length > 0 && (
              <div className="col-span-2">
                {/* Le tariffe degli interventi aggiuntivi passano in commessa
                    da sole: si mostrano perché sia chiaro con cosa verranno
                    valorizzate le attività fuori contratto nei SAL. */}
                <PannelloListino
                  voci={conv.voci} soloLettura mostraQuantita={false}
                  titolo="Tariffe per interventi aggiuntivi (dall'offerta)"
                  descrizione="Passano alla commessa: serviranno a valorizzare le attività aggiuntive nei SAL."
                  onChange={() => {}}
                />
              </div>
            )}

            {conv.modalita_esecuzione === 'economia' && (
              <div className="col-span-2">
                <PannelloListino voci={conv.voci} onChange={v => setConv(c => ({ ...c, voci: v }))} />
              </div>
            )}

            {conv.ereditate && (
              <div className="col-span-2 text-xs text-brand-800 bg-brand-50 border border-brand-200 rounded px-3 py-2">
                Importo, modalità e periodicità di fatturazione sono quelli concordati
                nell'offerta. Modificali qui se sono cambiati.
              </div>
            )}
            <div className="col-span-2">
              <label className="label">Modalità di fatturazione</label>
              <input className="input" placeholder="es. 50-50%, Fatturazione bimestrale…"
                     value={conv.tipo_fatturazione}
                     onChange={e => setConv(c => ({ ...c, tipo_fatturazione: e.target.value }))} />
            </div>
            {/* Acconto all'accettazione: la fattura nasce subito, con la
                competenza del mese in cui si accetta, e il riepilogo mensile la
                ripropone finché non la si spunta come emessa. */}
            <div className="col-span-2">
              <label className="label">
                Acconto da fatturare subito
                {conv.acconto?.percentuale
                  ? <span className="ml-1 text-xs text-gray-400 font-normal">
                      ({conv.acconto.percentuale}% — {conv.acconto.etichetta || 'acconto'}, dalle condizioni dell'offerta)
                    </span>
                  : <span className="ml-1 text-xs text-gray-400 font-normal">(vuoto o zero = nessun acconto)</span>}
              </label>
              <CampoImporto className="input" value={conv.acconto_importo}
                            onChange={v => setConv(c => ({ ...c, acconto_importo: v }))} />
              {Number(conv.acconto_importo) > 0 ? (
                <p className="text-xs text-gray-500 mt-0.5">
                  Finirà fra le fatture da emettere di <strong>{meseDi(conv.data_inizio)}</strong>, e resterà
                  nel riepilogo — segnalata come arretrata — finché non la spunti come emessa.
                  Il resto della commessa ({fmt(Math.max(0, (Number(conv.offerta.importo) || 0) - Number(conv.acconto_importo)))}) resta da fatturare.
                </p>
              ) : (
                <p className="text-xs text-gray-400 mt-0.5">
                  Nessuna fattura alla firma: tutto l'importo resta da fatturare a SAL o a rate.
                </p>
              )}
            </div>

            <div className="col-span-2">
              <label className="label">
                Periodicità
                <span className="ml-1 text-xs text-gray-400 font-normal">(se ricorrente, il sistema calcolerà quando emettere le fatture)</span>
              </label>
              <select className="input" value={conv.periodicita_mesi}
                      onChange={e => setConv(c => ({ ...c, periodicita_mesi: e.target.value }))}>
                <option value="">Non periodica (a SAL / milestone)</option>
                <option value="1">Mensile</option>
                <option value="2">Bimestrale</option>
                <option value="3">Trimestrale</option>
                <option value="4">Quadrimestrale</option>
                <option value="6">Semestrale</option>
              </select>
            </div>
          </div>

          <div className="flex justify-end gap-2 mt-5">
            <button className="btn-secondary" onClick={() => setConv(null)}>Annulla</button>
            <button className="btn-primary" onClick={confermaConversione} disabled={convertendo}>
              {convertendo ? 'Creo…' : '✓ Crea commessa'}
            </button>
          </div>
        </Modal>
      )}

      {sceltaRinnovo && (
        <SceltaDaRinnovare
          offerte={rows}
          onClose={() => setSceltaRinnovo(false)}
          onScelta={(r) => { setSceltaRinnovo(false); setRinnovo(r); }}
        />
      )}

      {rinnovo && (
        <RinnovoOfferta
          offerta={rinnovo}
          onClose={() => setRinnovo(null)}
          onFatto={(nuova) => {
            setRinnovo(null);
            load();
            alert(`Creata l'offerta ${nuova.numero} in bozza`
              + (nuova.adeguamento_applicato ? `, con adeguamento del ${String(nuova.adeguamento_applicato).replace('.', ',')}%` : ' agli stessi prezzi')
              + '. Aprila per rivederla prima di inviarla.');
          }}
        />
      )}

      {docOfferta && (
        <GeneraDocumento offerta={docOfferta} onClose={() => setDocOfferta(null)} />
      )}

      {allegatiDi && (
        <ModaleAllegati
          offerta={allegatiDi}
          onClose={() => setAllegatiDi(null)}
          onAggiornata={load}
          onCreaCommessa={(off) => { setAllegatiDi(null); apriConversione(off); }}
        />
      )}
    </div>
  );
}
